import React, { useState } from 'react';
import { LineChart, Line, AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import Icon from '../../../components/AppIcon';


const TradingChart = ({ selectedCrypto }) => {
  const [timeframe, setTimeframe] = useState('1D');
  const [chartType, setChartType] = useState('line');

  const timeframes = ['1H', '1D', '1W', '1M', '3M', '1Y', 'ALL'];

  const chartData = {
    '1H': [
      { time: '09:00', price: 42150, volume: 1200 },
      { time: '10:00', price: 42280, volume: 1450 },
      { time: '11:00', price: 42100, volume: 980 },
      { time: '12:00', price: 42350, volume: 1680 },
      { time: '13:00', price: 42500, volume: 2100 },
      { time: '14:00', price: 42420, volume: 1820 },
      { time: '15:00', price: 42650, volume: 2350 }
    ],
    '1D': [
      { time: '00:00', price: 41800, volume: 3200 },
      { time: '04:00', price: 42100, volume: 2800 },
      { time: '08:00', price: 42350, volume: 4100 },
      { time: '12:00', price: 42500, volume: 5200 },
      { time: '16:00', price: 42650, volume: 4800 },
      { time: '20:00', price: 42800, volume: 3900 },
      { time: '23:59', price: 42950, volume: 3500 }
    ],
    '1W': [
      { time: 'Mon', price: 40500, volume: 15000 },
      { time: 'Tue', price: 41200, volume: 18000 },
      { time: 'Wed', price: 41800, volume: 16500 },
      { time: 'Thu', price: 42100, volume: 19200 },
      { time: 'Fri', price: 42500, volume: 21000 },
      { time: 'Sat', price: 42800, volume: 17800 },
      { time: 'Sun', price: 42950, volume: 16200 }
    ],
    '1M': [
      { time: 'Week 1', price: 38500, volume: 85000 },
      { time: 'Week 2', price: 40200, volume: 92000 },
      { time: 'Week 3', price: 41500, volume: 88000 },
      { time: 'Week 4', price: 42950, volume: 95000 }
    ],
    '3M': [
      { time: 'Oct', price: 35000, volume: 280000 },
      { time: 'Nov', price: 38500, volume: 320000 },
      { time: 'Dec', price: 42950, volume: 350000 }
    ],
    '1Y': [
      { time: 'Jan', price: 28000, volume: 450000 },
      { time: 'Mar', price: 32000, volume: 520000 },
      { time: 'May', price: 35000, volume: 480000 },
      { time: 'Jul', price: 38000, volume: 550000 },
      { time: 'Sep', price: 40000, volume: 580000 },
      { time: 'Nov', price: 42000, volume: 620000 },
      { time: 'Dec', price: 42950, volume: 650000 }
    ],
    'ALL': [
      { time: '2020', price: 8000, volume: 1200000 },
      { time: '2021', price: 35000, volume: 2800000 },
      { time: '2022', price: 20000, volume: 2200000 },
      { time: '2023', price: 28000, volume: 2600000 },
      { time: '2024', price: 38000, volume: 3200000 },
      { time: '2025', price: 42950, volume: 3800000 }
    ]
  };

  const currentData = chartData?.[timeframe];
  const priceChange = currentData?.[currentData?.length - 1]?.price - currentData?.[0]?.price;
  const priceChangePercent = ((priceChange / currentData?.[0]?.price) * 100)?.toFixed(2);

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-card border border-border rounded-lg p-3 shadow-lg">
          <p className="text-xs text-muted-foreground mb-1">{payload?.[0]?.payload?.time}</p>
          <p className="text-sm font-semibold text-foreground">
            ${payload?.[0]?.value?.toLocaleString()}
          </p>
          {payload?.[1] && (
            <p className="text-xs text-muted-foreground mt-1">
              Vol: {payload?.[1]?.value?.toLocaleString()}
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  const renderChart = () => {
    const commonProps = {
      data: currentData,
      margin: { top: 10, right: 10, left: 0, bottom: 0 }
    };

    switch (chartType) {
      case 'area':
        return (
          <AreaChart {...commonProps}>
            <defs>
              <linearGradient id="colorPrice" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="var(--color-primary)" stopOpacity={0.3} />
                <stop offset="95%" stopColor="var(--color-primary)" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis dataKey="time" stroke="var(--color-muted-foreground)" style={{ fontSize: '12px' }} />
            <YAxis stroke="var(--color-muted-foreground)" style={{ fontSize: '12px' }} />
            <Tooltip content={<CustomTooltip />} />
            <Area type="monotone" dataKey="price" stroke="var(--color-primary)" fillOpacity={1} fill="url(#colorPrice)" />
          </AreaChart>
        );
      case 'bar':
        return (
          <BarChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis dataKey="time" stroke="var(--color-muted-foreground)" style={{ fontSize: '12px' }} />
            <YAxis stroke="var(--color-muted-foreground)" style={{ fontSize: '12px' }} />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="price" fill="var(--color-primary)" radius={[4, 4, 0, 0]} />
          </BarChart>
        );
      default:
        return (
          <LineChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis dataKey="time" stroke="var(--color-muted-foreground)" style={{ fontSize: '12px' }} />
            <YAxis stroke="var(--color-muted-foreground)" style={{ fontSize: '12px' }} />
            <Tooltip content={<CustomTooltip />} />
            <Line type="monotone" dataKey="price" stroke="var(--color-primary)" strokeWidth={2} dot={false} />
          </LineChart>
        );
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border p-4 md:p-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <h2 className="text-xl md:text-2xl font-semibold text-foreground">
              {selectedCrypto?.name}
            </h2>
            <span className="text-sm text-muted-foreground">{selectedCrypto?.symbol}</span>
          </div>
          <div className="flex items-baseline gap-3">
            <span className="text-2xl md:text-3xl font-bold text-foreground">
              ${currentData?.[currentData?.length - 1]?.price?.toLocaleString()}
            </span>
            <span className={`text-sm md:text-base font-medium ${priceChange >= 0 ? 'text-success' : 'text-error'}`}>
              {priceChange >= 0 ? '+' : ''}{priceChangePercent}%
            </span>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
            {['line', 'area', 'bar']?.map((type) => (
              <button
                key={type}
                onClick={() => setChartType(type)}
                className={`px-3 py-1.5 rounded-md text-xs md:text-sm font-medium transition-colors ${
                  chartType === type
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon 
                  name={type === 'line' ? 'TrendingUp' : type === 'area' ? 'Activity' : 'BarChart3'} 
                  size={16} 
                />
              </button>
            ))}
          </div>

          <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
            {timeframes?.map((tf) => (
              <button
                key={tf}
                onClick={() => setTimeframe(tf)}
                className={`px-2 md:px-3 py-1.5 rounded-md text-xs md:text-sm font-medium transition-colors ${
                  timeframe === tf
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {tf}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="w-full h-64 md:h-80 lg:h-96" aria-label={`${selectedCrypto?.name} price chart for ${timeframe} timeframe`}>
        <ResponsiveContainer width="100%" height="100%">
          {renderChart()}
        </ResponsiveContainer>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-6 border-t border-border">
        <div>
          <p className="text-xs md:text-sm text-muted-foreground mb-1">24h High</p>
          <p className="text-sm md:text-base font-semibold text-foreground">
            ${Math.max(...currentData?.map(d => d?.price))?.toLocaleString()}
          </p>
        </div>
        <div>
          <p className="text-xs md:text-sm text-muted-foreground mb-1">24h Low</p>
          <p className="text-sm md:text-base font-semibold text-foreground">
            ${Math.min(...currentData?.map(d => d?.price))?.toLocaleString()}
          </p>
        </div>
        <div>
          <p className="text-xs md:text-sm text-muted-foreground mb-1">24h Volume</p>
          <p className="text-sm md:text-base font-semibold text-foreground">
            ${currentData?.reduce((sum, d) => sum + d?.volume, 0)?.toLocaleString()}
          </p>
        </div>
        <div>
          <p className="text-xs md:text-sm text-muted-foreground mb-1">Market Cap</p>
          <p className="text-sm md:text-base font-semibold text-foreground">
            ${(currentData?.[currentData?.length - 1]?.price * 19500000)?.toLocaleString()}
          </p>
        </div>
      </div>
    </div>
  );
};

export default TradingChart;